import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity} from 'react-native';
import {
  Text, ActivityIndicator, Snackbar,
  Dialog, Portal, Button, TextInput,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import GradientHeader from '../../components/common/GradientHeader';
import {useNavigation} from '@react-navigation/native';
import {optionTypeApi} from '../../services/api/optionTypeApi';
import {colors, spacing} from '../../theme';

const RENDER_ICONS = {
  TOGGLE: 'toggle-switch-outline',
  SINGLE_SELECT: 'radiobox-marked',
  MULTI_SELECT: 'checkbox-multiple-marked-outline',
  TEXT: 'format-text',
};

const OptionTypeListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [optionTypes, setOptionTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await optionTypeApi.listConsolidatedOptionTypes(null, null, true);
      setOptionTypes(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggle = useCallback(async (item, newValue) => {
    try {
      await optionTypeApi.toggleOptionType(item.id, {isEnabled: newValue});
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchData, t]);

  const openEditDialog = useCallback((item) => {
    setEditingItem(item);
    setDisplayName(item.name);
    setSortOrder(String(item.displayOrder));
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingItem) return;
    try {
      await optionTypeApi.updateOverride(editingItem.id, {
        displayNameOverride: displayName.trim() || null,
        sortOrderOverride: sortOrder ? parseInt(sortOrder, 10) : null,
      });
      setDialogVisible(false);
      setEditingItem(null);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingItem, displayName, sortOrder, fetchData, t]);

  const sorted = optionTypes.slice().sort((a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0));
  const enabledCount = optionTypes.filter(ot => ot.enabled).length;

  if (isLoading && optionTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('optionType.title')} onBack={() => navigation.goBack()} />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>

        {optionTypes.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Icon name="tune-variant" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('optionType.noData')}</Text>
          </View>
        ) : (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Icon name="tune-variant" size={18} color={colors.primary} />
              <Text style={styles.sectionTitle}>Stitch Options</Text>
              <Text style={styles.sectionCount}>{enabledCount}/{optionTypes.length} active</Text>
            </View>
            <View style={styles.itemGrid}>
              {sorted.map(optionType => {
                const renderIcon = RENDER_ICONS[optionType.renderType] || 'format-list-bulleted';
                const optionCount = optionType.options?.length || 0;

                return (
                  <TouchableOpacity
                    key={optionType.id}
                    style={[styles.itemCard, !optionType.enabled && styles.itemCardDisabled]}
                    activeOpacity={0.7}
                    onPress={() => navigation.navigate('OptionTypeDetail', {optionType})}>
                    <View style={styles.itemRow}>
                      <TouchableOpacity
                        onPress={() => handleToggle(optionType, !optionType.enabled)}
                        hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
                        <Icon
                          name={optionType.enabled ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'}
                          size={22}
                          color={optionType.enabled ? colors.primary : colors.textLight}
                        />
                      </TouchableOpacity>
                      <View style={styles.itemContent}>
                        <Text style={[styles.optionName, !optionType.enabled && styles.textDisabled]} numberOfLines={1}>
                          {optionType.name}
                        </Text>
                        <View style={styles.itemMeta}>
                          <Icon name={renderIcon} size={12} color={colors.textSecondary} />
                          <Text style={styles.metaText}>{optionType.renderType?.replace('_', ' ')}</Text>
                          {optionCount > 0 && (
                            <Text style={styles.optionCount}> · {optionCount}</Text>
                          )}
                        </View>
                      </View>
                      <TouchableOpacity
                        onPress={() => openEditDialog(optionType)}
                        hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
                        style={styles.editBtn}>
                        <Icon name="pencil-outline" size={16} color={colors.textSecondary} />
                      </TouchableOpacity>
                      <Icon name="chevron-right" size={20} color={colors.textLight} />
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('optionType.editOverride')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('optionType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>{t('common.cancel')}</Button>
            <Button mode="contained" onPress={handleSaveOverride} style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {paddingBottom: 80},

  section: {
    marginBottom: spacing.md,
    paddingHorizontal: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text,
    flex: 1,
  },
  sectionCount: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    backgroundColor: colors.surface,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  itemGrid: {
    gap: spacing.sm,
  },
  itemCard: {
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  itemCardDisabled: {
    opacity: 0.45,
    borderColor: colors.divider,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  itemContent: {
    flex: 1,
  },
  editBtn: {
    padding: 4,
    backgroundColor: colors.background,
    borderRadius: 12,
  },
  optionName: {fontSize: 14, fontWeight: '600', color: colors.text},
  textDisabled: {color: colors.textSecondary},
  itemMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginTop: 2,
  },
  metaText: {fontSize: 10, fontWeight: '500', color: colors.textSecondary, textTransform: 'capitalize'},
  optionCount: {fontSize: 10, color: colors.textSecondary},

  emptyContainer: {alignItems: 'center', paddingTop: 80},
  emptyText: {textAlign: 'center', marginTop: spacing.sm, color: colors.textSecondary},
  dialogInput: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  saveButton: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10},
});

export default OptionTypeListScreen;
